import {
  attendanceValues,
  changeNotices,
  changeReasons,
  diagnoses,
  providerUnableToAttendReasons,
  technicalIssuesCancelSelections,
} from './endOfSessionConstants'
import { ICD10 } from '../../../models/icd10/ICD10'

export type endOfSessionMetadataValues = {
  attendance: string
  attendanceIneligibility?: boolean
  changeReason?: string
  technicalIssuesCancelSelection?: string
  technicalIssuesCancelDetails?: string
  providerInitiated?: string
  providerInitiatedDetails?: string
  changeNotice?: string
  changeNoticeDetail?: string
  diagnosis?: string[]
  primary_condition?: string[]
  other_primary_description?: string
  primary_condition_severity?: string
  secondary_condition?: string[] | null
  other_secondary_description?: string
  guardianConsent?: boolean
  is_last_session?: boolean
  episode_state?: string
  termination_comment?: string
  // Expected format: '{"type": "video" | "in_person" | "phone", "state"?: string, "zipcode"?: string}'
  modality?: string
  otherChangeReason?: string
  session_duration?: string
  reference_number?: string
  icd10_primary_diagnosis?: ICD10[]
  icd10_secondary_diagnosis?: ICD10[]
  attestation_pre_authorization?: string
  // Attestation data fields use dotted keys (e.g. `attestation_data.doesClientHigh_6_1`)
  [key: `attestation_data.${string}`]: unknown
}

export const endOfSessionMetaData = ({
  clientDetails,
  conditionsList,
  diagnosis,
  icd10_primary_diagnosis,
  icd10_secondary_diagnosis,
  canEndEpisode,
  primary_condition,
  other_primary_description,
  primary_condition_severity,
  secondary_condition,
  other_secondary_description,
  shouldShowGuardianConsent,
  terminationStatuses,
  isProviderEngageDA,
  isProviderCoach,
  modalityOptions,
  calculatedSessionRate,
  useEnhancedDiagnosisCapture,
  attendance,
  reference_number,
  is_last_session,
  episode_state,
  changeReason,
  changeNotice,
  technicalIssuesCancelSelection,
  technicalIssuesCancelDetails,
  session_duration,
  modality,
}: {
  clientDetails: { first_name: string }
  conditionsList: { label: string; value: string }[]
  diagnosis?: [{ label: string; value: string }] | string[]
  icd10_primary_diagnosis?: ICD10[]
  icd10_secondary_diagnosis?: ICD10[]
  canEndEpisode: boolean
  primary_condition?: string[] | undefined
  other_primary_description?: string
  primary_condition_severity?: string
  secondary_condition?: string[] | undefined
  other_secondary_description?: string
  shouldShowGuardianConsent: boolean
  terminationStatuses: { text: string; value: string; name: string; order: number }[]
  isProviderEngageDA: boolean
  isProviderCoach: boolean
  modalityOptions: {
    enum: string[]
    enumNames: string[]
  }
  calculatedSessionRate: string | number
  useEnhancedDiagnosisCapture: boolean
  attendance?: string
  reference_number?: string | null
  is_last_session?: boolean
  episode_state?: string
  changeReason?: string
  changeNotice?: string
  technicalIssuesCancelSelection?: string
  technicalIssuesCancelDetails?: string
  session_duration?: string
  modality?: string
}) => ({
  schema: {
    type: 'object',
    properties: {
      attendance: {
        name: 'attendance',
        type: 'string',
        title: 'Did this session happen?',
        enum: [attendanceValues.ATTENDED, attendanceValues.NO_SHOW, attendanceValues.INELIGIBLE],
        enumNames: ['Yes', 'No', `Yes, but ${clientDetails.first_name} is ineligible to continue care`],
        validation: {
          isRequired: true,
        },
      },
      ...(isProviderEngageDA && {
        modality: {
          name: 'modality',
          type: 'string',
          title: 'Session Modality',
          enum: modalityOptions.enum,
          enumNames: modalityOptions.enumNames,
          condition: {
            parentField: 'attendance',
            parentValue: attendanceValues.ATTENDED,
          },
          validation: {
            isRequired: true,
          },
        },
      }),
      ...(!isProviderEngageDA && {
        attendanceIneligibility: {
          name: 'attendanceIneligibility',
          type: 'boolean',
          title:
            'Have you submitted a ticket with Platform Support and received confirmation that the client is ineligible for care?',
          condition: {
            parentField: 'attendance',
            parentValue: attendanceValues.INELIGIBLE,
          },
          validation: {
            hasValue: 1,
          },
        },
      }),
      attendanceIneligibilityMessage: {
        content: 'Please confirm with Platform Support whether or not the client is eligible to continue with care.',
        condition: {
          conditionType: 'AND',
          items: [
            {
              parentField: 'attendance',
              parentValue: attendanceValues.INELIGIBLE,
            },
            {
              parentField: 'attendanceIneligibility',
              parentValue: 0,
            },
          ],
        },
      },
      changeReason: {
        name: 'changeReason',
        type: 'string',
        title: "Why didn't this session happen? \n Please consider your selection carefully.",
        enum: changeReasons(clientDetails.first_name, isProviderEngageDA).map(({ value }) => value),
        enumNames: changeReasons(clientDetails.first_name, isProviderEngageDA).map(({ text }) => text),
        condition: {
          parentField: 'attendance',
          parentValue: attendanceValues.NO_SHOW,
        },
        validation: {
          isRequired: true,
        },
      },
      technicalIssuesCancelSelection: {
        name: 'technicalIssuesCancelSelection',
        type: 'string',
        title:
          'Which of the following best describes the “technical issue” that prevented this session from occurring?',
        enum: technicalIssuesCancelSelections(clientDetails.first_name).map(({ value }) => value),
        enumNames: technicalIssuesCancelSelections(clientDetails.first_name).map(({ text }) => text),
        condition: {
          parentField: 'changeReason',
          parentValue: 'technicalIssues',
        },
        validation: {
          isRequired: true,
        },
      },
      technicalIssuesCancelDetails: {
        name: 'technicalIssuesCancelDetails',
        type: 'string',
        title: 'Additional details on technical issue(s) experienced:',
        condition: {
          parentField: 'changeReason',
          parentValue: 'technicalIssues',
        },
      },
      ...(isProviderEngageDA && {
        session_duration: {
          name: 'session_duration',
          type: 'string',
          title: 'How long was the session? Sessions under 15 min are not reimbursed.',
          enum: ['30', '45', '60'],
          enumNames: ['30 (16 to 37 minutes)', '45 (38 to 52 minutes)', '60 (53 minutes or above)'],
          condition: {
            parentField: 'attendance',
            parentValue: attendanceValues.ATTENDED,
          },
          validation: {
            isRequired: true,
          },
        },
        otherChangeReason: {
          name: 'otherChangeReason',
          type: 'string',
          title: 'Since you selected "Other", please describe the reason why the session did not happen',
          condition: {
            parentField: 'changeReason',
            parentValue: 'other',
          },
          validation: {
            isRequired: true,
          },
        },
      }),
      ...(!isProviderEngageDA && {
        providerInitiated: {
          name: 'providerInitiated',
          type: 'string',
          title: 'What was the situation?',
          enum: providerUnableToAttendReasons().map(({ value }) => value),
          enumNames: providerUnableToAttendReasons().map(({ text }) => text),
          condition: {
            parentField: 'changeReason',
            parentValue: 'providerInitiated',
          },
          validation: {
            isRequired: true,
          },
        },
        providerInitiatedDetails: {
          name: 'providerInitiatedDetails',
          type: 'string',
          title: 'Additional details on this cancellation',
          condition: {
            parentField: 'providerInitiated',
            parentValue: 'providerUnableToAttendReasonOtherReason',
          },
          validation: {
            isRequired: true,
          },
        },
      }),
      changeNotice: {
        name: 'changeNotice',
        type: 'string',
        title: 'What was the situation?',
        enum: changeNotices(isProviderEngageDA).map(({ value }) => value),
        enumNames: changeNotices(isProviderEngageDA).map(({ text }) => text),
        condition: {
          parentField: 'changeReason',
          parentValue: 'clientCancel',
        },
        validation: {
          isRequired: true,
        },
      },
      ...(isProviderEngageDA && {
        reference_number: {
          name: 'reference_number',
          title: 'Reference number (optional)',
          type: 'string',
          validation: {
            isRequired: false,
          },
        },
        sessionRateMessage: {
          name: 'sessionRateMessage',
          type: 'custom',
          sessionRate: calculatedSessionRate,
          condition: {
            parentField: 'attendance',
            parentValue: attendanceValues.ATTENDED,
          },
        },
        sessionRateMessageInEligible: {
          name: 'sessionRateMessageInEligible',
          type: 'custom',
          isIneligible: true,
          condition: {
            parentField: 'attendance',
            parentValue: attendanceValues.INELIGIBLE,
          },
        },
        diagnosisAndTreatmentHeader: {
          name: 'diagnosisAndTreatmentHeader',
          content: '## Treatment focus',
          sectionHeader: true,
          condition: {
            parentField: 'attendance',
            parentValue: attendanceValues.ATTENDED,
          },
        },
        diagnosisAndTreatmentDescription: {
          name: 'diagnosisAndTreatmentDescription',
          content:
            'List primary and secondary clinical targets for this treatment episode, as permitted by applicable law.',
          condition: {
            parentField: 'attendance',
            parentValue: attendanceValues.ATTENDED,
          },
        },
      }),
      ...(!useEnhancedDiagnosisCapture && {
        diagnosis: {
          name: 'diagnosis',
          title: 'Diagnosis',
          type: 'string',
          options: diagnoses,
          multiSelect: true,
          placeholder: 'Select diagnosis',
          validation: {
            isRequired: true,
          },
          condition: {
            parentField: 'attendance',
            parentValue: attendanceValues.ATTENDED,
          },
        },
        other_diagnosis: {
          name: 'other_diagnosis',
          title: 'Since you selected "Other", please describe the diagnosis',
          type: 'string',
          condition: { parentField: 'diagnosis', parentValue: ['Other'], match: 'oneOf' },
          ...(!isProviderCoach && { validation: { isRequired: true } }),
        },
      }),
      ...(useEnhancedDiagnosisCapture && {
        primary_condition: {
          name: 'primary_condition',
          type: 'string',
          title: 'Indicate the primary condition that you are targeting in therapy:',
          options: conditionsList,
          multiSelect: false,
          saveFieldAsArray: true,
          validation: {
            isRequired: true,
          },
          condition: {
            parentField: 'attendance',
            parentValue: attendanceValues.ATTENDED,
          },
        },
        other_primary_description: {
          name: 'other_primary_description',
          title: 'Since you selected “Other,” please describe the condition',
          type: 'string',
          condition: {
            parentField: 'primary_condition',
            parentValue: ['Other'],
          },
          validation: {
            isRequired: true,
          },
        },
        ...(!isProviderEngageDA && {
          primary_condition_severity: {
            name: 'primary_condition_severity',
            type: 'string',
            title: 'For this client’s primary condition, indicate the severity:',
            enum: ['mild', 'moderate', 'severe'],
            enumNames: ['Mild', 'Moderate', 'Severe'],
            validation: {
              isRequired: true,
            },
            condition: {
              parentField: 'attendance',
              parentValue: attendanceValues.ATTENDED,
            },
          },
        }),
        secondary_condition: {
          name: 'secondary_condition',
          title: 'Indicate any secondary, co-occurring conditions (Select all that apply):',
          type: 'string',
          options: conditionsList,
          placeholder: 'Select diagnosis',
          condition: {
            parentField: 'attendance',
            parentValue: attendanceValues.ATTENDED,
          },
        },
        other_secondary_description: {
          name: 'other_secondary_description',
          title: 'Since you selected “Other,” please describe the condition',
          type: 'string',
          condition: {
            parentField: 'secondary_condition',
            parentValue: ['Other'],
          },
          validation: {
            isRequired: true,
          },
        },
      }),
      ...(shouldShowGuardianConsent && {
        guardianConsent: {
          name: 'guardianConsent',
          type: 'string',
          title:
            'You must receive written guardian consent prior to closing session 1. Have you received this consent?',
          enum: [true, false],
          enumNames: [
            `Yes, I acknowledge that I have received written consent from ${clientDetails.first_name}’s guardian.`,
            `No, it has been 72 hours since the first session and I have not received written consent, and so will not continue care with ${clientDetails.first_name}. I have informed ${clientDetails.first_name} that I cannot continue with care. I will close the episode.`,
          ],
          condition: {
            parentField: 'attendance',
            parentValue: attendanceValues.ATTENDED,
          },
        },
      }),
      ...(canEndEpisode && {
        is_last_session: {
          name: 'is_last_session',
          type: 'string',
          title: isProviderEngageDA
            ? 'Do you want to discharge this client?'
            : 'Do you want to end the episode right now?',
          enum: [true, false],
          enumNames: [
            isProviderEngageDA
              ? 'Yes, I want to remove this client from my active client list'
              : 'Yes, I want to close the episode',
            'No',
          ],
          validation: {
            isRequired: true,
          },
        },
      }),
      episode_state: {
        name: 'episode_state',
        type: 'string',
        multiSelect: false,
        title: `What is the reason for ${isProviderEngageDA ? 'discharging this client?' : 'ending the episode?'}`,
        enum: terminationStatuses.sort((a, b) => a.order - b.order).map(({ value }: any) => value),
        enumNames: terminationStatuses.sort((a, b) => a.order - b.order).map(({ text }: any) => text),
        condition: {
          parentField: 'is_last_session',
          parentValue: true,
        },
        validation: {
          isRequired: true,
        },
      },
      termination_comment: {
        name: 'termination_comment',
        type: 'string',
        title: 'Comment',
        condition: {
          parentField: 'is_last_session',
          parentValue: true,
        },
      },
    },
  },
  uiSchema: {
    'ui:order': [
      'attendance',
      'modality',
      'sessionRateMessageInEligible',
      'attendanceIneligibility',
      'attendanceIneligibilityMessage',
      'changeReason',
      'otherChangeReason',
      'technicalIssuesCancelSelection',
      'technicalIssuesCancelDetails',
      'providerInitiated',
      'providerInitiatedDetails',
      'changeNotice',
      'changeNoticeDetail',
      'session_duration',
      'reference_number',
      'sessionRateMessage',
      'diagnosisAndTreatmentHeader',
      'diagnosisAndTreatmentDescription',
      'diagnosis',
      'other_diagnosis',
      'primary_condition',
      'other_primary_description',
      'primary_condition_severity',
      'secondary_condition',
      'other_secondary_description',
      'guardianConsent',
      'is_last_session',
      'episode_state',
      'termination_comment',
    ],
    attendance: {
      'ui:widget': 'radio',
      'ui:options': { styleType: '' },
    },
    modality: {
      'ui:widget': 'radio',
      'ui:options': { styleType: '' },
    },
    attendanceIneligibility: {
      'ui:widget': 'radio',
      'ui:options': { styleType: '' },
    },
    changeReason: {
      'ui:widget': 'radio',
      'ui:options': { styleType: '' },
    },
    technicalIssuesCancelSelection: {
      'ui:widget': 'radio',
      'ui:options': { styleType: '' },
    },
    technicalIssuesCancelDetails: {
      'ui:widget': 'textarea',
    },
    providerInitiated: {
      'ui:widget': 'radio',
      'ui:options': { styleType: '' },
    },
    providerInitiatedDetails: {
      'ui:widget': 'textarea',
    },
    changeNotice: {
      'ui:widget': 'radio',
      'ui:options': { styleType: '' },
    },
    changeNoticeDetail: {
      'ui:widget': 'radio',
      'ui:options': { styleType: '' },
    },
    session_duration: {
      'ui:widget': 'radio',
      'ui:options': { styleType: '' },
    },
    reference_number: {
      'ui:widget': 'text',
      'ui:help': 'some text to help understand the reference number',
    },
    sessionRateMessage: {
      'ui:field': 'DASessionRate',
    },
    sessionRateMessageInEligible: {
      'ui:field': 'DASessionRate',
    },
    diagnosis: {
      'ui:widget': 'typeAhead',
    },
    other_diagnosis: {
      'ui:widget': 'text',
    },
    otherChangeReason: {
      'ui:widget': 'text',
    },
    primary_condition: {
      'ui:widget': 'typeAhead',
    },
    other_primary_description: {
      'ui:widget': 'text',
    },
    primary_condition_severity: {
      'ui:widget': 'radio',
    },
    secondary_condition: {
      'ui:widget': 'typeAhead',
    },
    other_secondary_description: {
      'ui:widget': 'text',
    },
    guardianConsent: {
      'ui:widget': 'radio',
    },
    is_last_session: {
      'ui:widget': 'radio',
      'ui:options': { styleType: '' },
    },
    episode_state: {
      'ui:widget': 'select',
    },
    termination_comment: {
      'ui:widget': 'textarea',
    },
  },
  initialValues: {
    is_last_session: is_last_session ?? false,
    ...(diagnosis && { diagnosis }),
    ...(primary_condition && { primary_condition }),
    ...(other_primary_description && { other_primary_description }),
    ...(secondary_condition && { secondary_condition }),
    ...(other_secondary_description && { other_secondary_description }),
    ...(primary_condition_severity && { primary_condition_severity }),
    ...(icd10_primary_diagnosis && { icd10_primary_diagnosis }),
    ...(icd10_secondary_diagnosis && { icd10_secondary_diagnosis }),
    ...(attendance && { attendance }),
    ...(reference_number && { reference_number }),
    ...(episode_state && { episode_state }),
    ...(changeReason && { changeReason }),
    ...(changeNotice && { changeNotice }),
    ...(technicalIssuesCancelSelection && { technicalIssuesCancelSelection }),
    ...(technicalIssuesCancelDetails && { technicalIssuesCancelDetails }),
    ...(session_duration && { session_duration }),
    ...(modality && { modality }),
  },
})
